import { ShieldCheck, Sparkles, Radar, BellRing, Siren } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useActivateWorkspace } from '@/api/healthcheck';
import { OnboardingCss } from './onboarding-css';
import {
  heroWrap,
  heroIcon,
  heroEyebrow,
  heroTitle,
  heroLede,
  stepList,
  stepRow,
  markerAccent,
  stepTitle,
  stepDesc,
} from './onboarding-styles';

export interface WorkspaceActivationProps {
  /** Optional callback after a successful activation (queries are auto-invalidated). */
  onActivated?: () => void;
}

const PERKS = [
  { icon: <Radar size={16} />, title: 'Uptime monitors', desc: 'Watch endpoints and services with HTTP, TCP, ping and keyword checks.' },
  { icon: <BellRing size={16} />, title: 'Alerting channels', desc: 'Get notified via Telegram, email, Slack, webhooks and Teams.' },
  { icon: <Siren size={16} />, title: 'Incidents & alarms', desc: 'Track incidents and latency with automatic alarms and playbooks.' },
];

/**
 * State A — the organization has no Healthcheck workspace yet (backend answered 403
 * / no-tenant). Instead of a red error, present a friendly call to action that
 * actually creates the workspace via `POST /workspace` (user-driven get-or-create).
 * On success the hook invalidates the healthcheck + projects caches, so whatever
 * screen rendered this (picker / dashboard) re-fetches and recovers on its own.
 */
export function WorkspaceActivation({ onActivated }: WorkspaceActivationProps) {
  const activate = useActivateWorkspace({ onSuccess: () => onActivated?.() });

  return (
    <div className="vn-ob-hero" style={heroWrap}>
      <OnboardingCss />

      <span className="vn-ob-halo" style={heroIcon} aria-hidden>
        <ShieldCheck size={30} />
      </span>

      <p style={heroEyebrow}>Welcome to Healthcheck</p>
      <h2 style={heroTitle}>Activate monitoring for your organization</h2>
      <p style={heroLede}>
        Create your monitoring workspace to start adding monitors, channels and
        alarms. It only takes a moment — we&apos;ll set everything up for your org.
      </p>

      <Button
        size="lg"
        loading={activate.isPending}
        startIcon={<Sparkles size={16} />}
        onClick={() => activate.mutate()}
        disabled={activate.isPending}
        style={{ marginTop: 'var(--vn-space-2)' }}
      >
        {activate.isPending ? 'Creating…' : 'Create workspace'}
      </Button>

      {activate.isError && (
        <Alert
          tone="danger"
          title="Couldn't create the workspace"
          actions={
            <Button size="sm" variant="secondary" onClick={() => activate.reset()}>
              Dismiss
            </Button>
          }
          style={{ marginTop: 'var(--vn-space-2)', textAlign: 'left', maxWidth: 460 }}
        >
          {activate.error?.message || 'Please try again, or contact your account team if it persists.'}
        </Alert>
      )}

      <div style={{ ...stepList, marginTop: 'var(--vn-space-5)', width: '100%', maxWidth: 520, textAlign: 'left' }}>
        {PERKS.map((p) => (
          <div key={p.title} style={stepRow}>
            <span style={markerAccent}>{p.icon}</span>
            <div style={{ display: 'grid', gap: 2 }}>
              <p style={stepTitle}>{p.title}</p>
              <p style={stepDesc}>{p.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <p style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)', margin: 0 }}>
        Trouble activating? Contact your account team to enable the Healthcheck service.
      </p>
    </div>
  );
}
